import { alpha, createTheme, lighten, darken } from '@mui/material';
import '@mui/lab/themeAugmentation';

// ZPS Studio Color Tokens
export const themeColors = {
  primary: '#5B4DFF',       // Brand Blue
  secondary: '#00B0FF',     // Brand Light Blue
  success: '#00D68F',       // CTA Green
  warning: '#FFD600',       // Warning Yellow
  error: '#FF1744',         // Danger Red
  info: '#4A90D9',          // Info Blue
  black: '#000000',
  white: '#ffffff',
  purple: '#7C4DFF',
  bgDark: '#13151F',        // Main background
  bgSurface: '#252836',     // Cards, panels
  bgElevated: '#2E3148',    // Hover states
  sidebarBg: '#1A1D2E',     // Sidebar background
  textPrimary: '#FFFFFF',
  textSecondary: '#8A8FA8',
};

export const colors = {
  gradients: {
    blue1: 'linear-gradient(135deg, #4A90D9 0%, #7C4DFF 100%)',
    blue2: 'linear-gradient(135deg, #ABDCFF 0%, #0396FF 100%)',
    blue3: 'linear-gradient(127.55deg, #141E30 3.73%, #243B55 92.26%)',
    blue4: 'linear-gradient(-20deg, #2b5876 0%, #4e4376 100%)',
    blue5: 'linear-gradient(135deg, #97ABFF 10%, #123597 100%)',
    orange1: 'linear-gradient(135deg, #FCCF31 0%, #F55555 100%)',
    orange2: 'linear-gradient(135deg, #FFD3A5 0%, #FD6585 100%)',
    orange3: 'linear-gradient(120deg, #f6d365 0%, #fda085 100%)',
    purple1: 'linear-gradient(135deg, #43CBFF 0%, #9708CC 100%)',
    purple3: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    pink1: 'linear-gradient(135deg, #F6CEEC 0%, #D939CD 100%)',
    pink2: 'linear-gradient(135deg, #F761A1 0%, #8C1BAB 100%)',
    green1: 'linear-gradient(135deg, #FFF720 0%, #3CD500 100%)',
    green2: 'linear-gradient(to bottom, #00b09b, #96c93d)',
    black1: 'linear-gradient(100.66deg, #434343 6.56%, #000000 93.57%)',
    black2: 'linear-gradient(60deg, #29323c 0%, #485563 100%)',
    // ZPS Brand gradients
    brand: 'linear-gradient(135deg, #2979FF 0%, #00B0FF 100%)',
    brandPurple: 'linear-gradient(135deg, #7C4DFF 0%, #B47AFF 50%, #2979FF 100%)',
    brandBlue: 'linear-gradient(135deg, #4A90D9 0%, #7C4DFF 100%)',
    brandGreen: 'linear-gradient(135deg, #00D68F 0%, #4A90D9 100%)',
  },
  shadows: {
    success: '0px 1px 4px rgba(0, 214, 143, 0.25), 0px 3px 12px 2px rgba(0, 214, 143, 0.35)',
    error: '0px 1px 4px rgba(232, 52, 26, 0.25), 0px 3px 12px 2px rgba(232, 52, 26, 0.35)',
    info: '0px 1px 4px rgba(74, 144, 217, 0.25), 0px 3px 12px 2px rgba(74, 144, 217, 0.35)',
    primary: '0px 1px 4px rgba(232, 52, 26, 0.25), 0px 3px 12px 2px rgba(232, 52, 26, 0.35)',
    warning: '0px 1px 4px rgba(245, 166, 35, 0.25), 0px 3px 12px 2px rgba(245, 166, 35, 0.35)',
    card: '0px 4px 16px rgba(0, 0, 0, 0.3)',
    cardSm: '0px 2px 8px rgba(0, 0, 0, 0.25)',
    cardLg: '0 8px 24px rgba(0, 0, 0, 0.4)',
  },
  layout: {
    general: {
      bodyBg: themeColors.bgDark,
    },
    sidebar: {
      background: themeColors.sidebarBg,
      textColor: themeColors.textSecondary,
      dividerBg: 'rgba(255,255,255,0.06)',
      menuItemColor: themeColors.textSecondary,
      menuItemColorActive: themeColors.white,
      menuItemBg: 'transparent',
      menuItemBgActive: 'linear-gradient(135deg, #2979FF, #00B0FF)',
      menuItemIconColor: themeColors.textSecondary,
      menuItemIconColorActive: themeColors.white,
      menuItemHeadingColor: themeColors.textSecondary,
    },
  },
  alpha: {
    white: {
      5: alpha(themeColors.white, 0.02),
      10: alpha(themeColors.white, 0.1),
      30: alpha(themeColors.white, 0.3),
      50: alpha(themeColors.white, 0.5),
      70: alpha(themeColors.white, 0.7),
      100: themeColors.white,
    },
    trueWhite: {
      5: alpha(themeColors.white, 0.02),
      10: alpha(themeColors.white, 0.1),
      30: alpha(themeColors.white, 0.3),
      50: alpha(themeColors.white, 0.5),
      70: alpha(themeColors.white, 0.7),
      100: themeColors.white,
    },
    black: {
      5: alpha(themeColors.black, 0.02),
      10: alpha(themeColors.black, 0.1),
      30: alpha(themeColors.black, 0.3),
      50: alpha(themeColors.black, 0.5),
      70: alpha(themeColors.black, 0.7),
      100: themeColors.black,
    },
    lightGreen: {
      5: alpha(themeColors.success, 0.02),
      10: alpha(themeColors.success, 0.1),
      30: alpha(themeColors.success, 0.3),
      50: alpha(themeColors.success, 0.5),
      70: alpha(themeColors.success, 0.7),
      100: themeColors.success,
    },
  },
  secondary: {
    lighter: alpha(themeColors.secondary, 0.1),
    light: lighten(themeColors.secondary, 0.3),
    main: themeColors.secondary,
    dark: darken(themeColors.secondary, 0.2),
  },
  primary: {
    lighter: alpha(themeColors.primary, 0.1),
    light: lighten(themeColors.primary, 0.3),
    main: themeColors.primary,
    dark: darken(themeColors.primary, 0.2),
  },
  success: {
    lighter: alpha(themeColors.success, 0.1),
    light: lighten(themeColors.success, 0.3),
    main: themeColors.success,
    dark: darken(themeColors.success, 0.2),
  },
  warning: {
    lighter: alpha(themeColors.warning, 0.1),
    light: lighten(themeColors.warning, 0.3),
    main: themeColors.warning,
    dark: darken(themeColors.warning, 0.2),
  },
  error: {
    lighter: alpha(themeColors.error, 0.1),
    light: lighten(themeColors.error, 0.3),
    main: themeColors.error,
    dark: darken(themeColors.error, 0.2),
  },
  info: {
    lighter: alpha(themeColors.info, 0.1),
    light: lighten(themeColors.info, 0.3),
    main: themeColors.info,
    dark: darken(themeColors.info, 0.2),
  },
};

export const ZPSDarkTheme = createTheme({
  palette: {
    common: {
      black: colors.alpha.black[100],
      white: colors.alpha.white[100],
    },
    mode: 'dark',
    primary: {
      light: colors.primary.light,
      main: colors.primary.main,
      dark: colors.primary.dark,
    },
    secondary: {
      light: colors.secondary.light,
      main: colors.secondary.main,
      dark: colors.secondary.dark,
    },
    error: {
      light: colors.error.light,
      main: colors.error.main,
      dark: colors.error.dark,
      contrastText: colors.alpha.white[100],
    },
    success: {
      light: colors.success.light,
      main: colors.success.main,
      dark: colors.success.dark,
      contrastText: colors.alpha.white[100],
    },
    info: {
      light: colors.info.light,
      main: colors.info.main,
      dark: colors.info.dark,
      contrastText: colors.alpha.white[100],
    },
    warning: {
      light: colors.warning.light,
      main: colors.warning.main,
      dark: colors.warning.dark,
      contrastText: colors.alpha.white[100],
    },
    text: {
      primary: themeColors.textPrimary,
      secondary: themeColors.textSecondary,
      disabled: alpha(themeColors.white, 0.4),
    },
    background: {
      paper: themeColors.bgSurface,
      default: themeColors.bgDark,
    },
    action: {
      active: alpha(themeColors.white, 0.7),
      hover: alpha(themeColors.white, 0.04),
      hoverOpacity: 0.04,
      selected: alpha(themeColors.white, 0.08),
      selectedOpacity: 0.08,
      disabled: alpha(themeColors.white, 0.3),
      disabledBackground: alpha(themeColors.white, 0.05),
      disabledOpacity: 0.38,
      focus: alpha(themeColors.white, 0.1),
      focusOpacity: 0.05,
      activatedOpacity: 0.12,
    },
    tonalOffset: 0.5,
    divider: 'rgba(255,255,255,0.06)',
  },
  colors: {
    layout: {
      general: {
        bodyBg: colors.layout.general.bodyBg,
      },
    },
    gradients: {
      blue1: colors.gradients.blue1,
      blue2: colors.gradients.blue2,
      blue3: colors.gradients.blue3,
      blue4: colors.gradients.blue4,
      blue5: colors.gradients.blue5,
      orange1: colors.gradients.orange1,
      orange2: colors.gradients.orange2,
      orange3: colors.gradients.orange3,
      purple1: colors.gradients.purple1,
      purple3: colors.gradients.purple3,
      pink1: colors.gradients.pink1,
      pink2: colors.gradients.pink2,
      green1: colors.gradients.green1,
      green2: colors.gradients.green2,
      black1: colors.gradients.black1,
      black2: colors.gradients.black2,
    },
    shadows: {
      success: colors.shadows.success,
      error: colors.shadows.error,
      primary: colors.shadows.primary,
      info: colors.shadows.info,
      warning: colors.shadows.warning,
    },
    alpha: {
      white: {
        5: alpha(themeColors.white, 0.02),
        10: alpha(themeColors.white, 0.1),
        30: alpha(themeColors.white, 0.3),
        50: alpha(themeColors.white, 0.5),
        70: alpha(themeColors.white, 0.7),
        100: themeColors.white,
      },
      trueWhite: {
        5: alpha(themeColors.white, 0.02),
        10: alpha(themeColors.white, 0.1),
        30: alpha(themeColors.white, 0.3),
        50: alpha(themeColors.white, 0.5),
        70: alpha(themeColors.white, 0.7),
        100: themeColors.white,
      },
      black: {
        5: alpha(themeColors.black, 0.02),
        10: alpha(themeColors.black, 0.1),
        30: alpha(themeColors.black, 0.3),
        50: alpha(themeColors.black, 0.5),
        70: alpha(themeColors.black, 0.7),
        100: themeColors.black,
      },
      lightGreen: {
        5: alpha(themeColors.success, 0.02),
        10: alpha(themeColors.success, 0.1),
        30: alpha(themeColors.success, 0.3),
        50: alpha(themeColors.success, 0.5),
        70: alpha(themeColors.success, 0.7),
        100: themeColors.success,
      },
    },
    secondary: {
      lighter: alpha(themeColors.secondary, 0.1),
      light: lighten(themeColors.secondary, 0.3),
      main: themeColors.secondary,
      dark: darken(themeColors.secondary, 0.2),
    },
    primary: {
      lighter: alpha(themeColors.primary, 0.1),
      light: lighten(themeColors.primary, 0.3),
      main: themeColors.primary,
      dark: darken(themeColors.primary, 0.2),
    },
    success: {
      lighter: alpha(themeColors.success, 0.1),
      light: lighten(themeColors.success, 0.3),
      main: themeColors.success,
      dark: darken(themeColors.success, 0.2),
    },
    warning: {
      lighter: alpha(themeColors.warning, 0.1),
      light: lighten(themeColors.warning, 0.3),
      main: themeColors.warning,
      dark: darken(themeColors.warning, 0.2),
    },
    error: {
      lighter: alpha(themeColors.error, 0.1),
      light: lighten(themeColors.error, 0.3),
      main: themeColors.error,
      dark: darken(themeColors.error, 0.2),
    },
    info: {
      lighter: alpha(themeColors.info, 0.1),
      light: lighten(themeColors.info, 0.3),
      main: themeColors.info,
      dark: darken(themeColors.info, 0.2),
    },
  },
  shape: {
    borderRadius: 12,
  },
  header: {
    height: '70px',
    heightUpper: '70px',
    heightLower: '0px',
    background: themeColors.bgSurface,
    boxShadow: '0 1px 0 rgba(255,255,255,0.06)',
    textColor: themeColors.textSecondary,
  },
  general: {
    reactFrameworkColor: '#00D8FF',
    borderRadiusSm: '8px',
    borderRadius: '12px',
    borderRadiusLg: '16px',
    borderRadiusXl: '24px',
  },
  sidebar: {
    background: colors.layout.sidebar.background,
    textColor: colors.layout.sidebar.textColor,
    dividerBg: colors.layout.sidebar.dividerBg,
    menuItemColor: colors.layout.sidebar.menuItemColor,
    menuItemColorActive: colors.layout.sidebar.menuItemColorActive,
    menuItemBg: colors.layout.sidebar.menuItemBg,
    menuItemBgActive: colors.layout.sidebar.menuItemBgActive,
    menuItemIconColor: colors.layout.sidebar.menuItemIconColor,
    menuItemIconColorActive: colors.layout.sidebar.menuItemIconColorActive,
    menuItemHeadingColor: colors.layout.sidebar.menuItemHeadingColor,
    boxShadow: '1px 0 0 rgba(255,255,255,0.06)',
    width: '260px',
  },
  spacing: 9,
  breakpoints: {
    values: {
      xs: 0,
      sm: 640,
      md: 1024,
      lg: 1280,
      xl: 1440,
    },
  },
  components: {
    MuiBackdrop: {
      styleOverrides: {
        root: {
          backgroundColor: alpha(themeColors.black, 0.6),
          backdropFilter: 'blur(8px)',
          '&.MuiBackdrop-invisible': {
            backgroundColor: 'transparent',
            backdropFilter: 'blur(2px)',
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          marginLeft: 8,
          marginRight: 8,
          fontWeight: 'bold',
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        'html, body': {
          width: '100%',
          minHeight: '100%',
        },
        body: {
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          width: '100%',
          flex: 1,
          backgroundColor: themeColors.bgDark,
          color: themeColors.textPrimary,
        },
        '#root': {
          width: '100%',
          minHeight: '100vh',
          display: 'flex',
          flex: 1,
          flexDirection: 'column',
        },
        html: {
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100%',
          width: '100%',
          MozOsxFontSmoothing: 'grayscale',
          WebkitFontSmoothing: 'antialiased',
        },
        '.child-popover .MuiPaper-root .MuiList-root': {
          flexDirection: 'column',
        },
        '#nprogress': {
          pointerEvents: 'none',
        },
        '#nprogress .bar': {
          background: colors.gradients.brand,
        },
        '#nprogress .spinner-icon': {
          borderTopColor: themeColors.primary,
          borderLeftColor: themeColors.primary,
        },
        '#nprogress .peg': {
          boxShadow: '0 0 15px ' + themeColors.primary + ', 0 0 8px ' + themeColors.secondary,
        },
        ':root': {
          '--swiper-theme-color': themeColors.primary,
          '--zps-brand-red': '#2979FF',
          '--zps-brand-orange': '#00B0FF',
          '--zps-bg-dark': '#13151F',
          '--zps-bg-surface': '#252836',
          '--zps-bg-elevated': '#2E3148',
          '--zps-text-primary': '#FFFFFF',
          '--zps-text-secondary': '#8A8FA8',
          '--zps-accent-green': '#00D68F',
          '--zps-accent-purple': '#7C4DFF',
          '--zps-accent-blue': '#4A90D9',
        },
        code: {
          background: alpha(themeColors.info, 0.1),
          color: lighten(themeColors.info, 0.3),
          borderRadius: 4,
          padding: 4,
        },
        '@keyframes ripple': {
          '0%': { transform: 'scale(.8)', opacity: 1 },
          '100%': { transform: 'scale(2.8)', opacity: 0 },
        },
        '@keyframes float': {
          '0%': { transform: 'translate(0%, 0%)' },
          '100%': { transform: 'translate(3%, 3%)' },
        },
        '@keyframes shimmer': {
          '0%': { backgroundPosition: '-200% 0' },
          '100%': { backgroundPosition: '200% 0' },
        },
        // Scrollbar styling for dark theme
        '*::-webkit-scrollbar': {
          width: '8px',
          height: '8px',
        },
        '*::-webkit-scrollbar-track': {
          background: themeColors.bgDark,
        },
        '*::-webkit-scrollbar-thumb': {
          background: alpha(themeColors.white, 0.15),
          borderRadius: '4px',
        },
        '*::-webkit-scrollbar-thumb:hover': {
          background: alpha(themeColors.white, 0.25),
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        iconOutlined: {
          color: themeColors.textSecondary,
        },
        icon: {
          top: 'calc(50% - 14px)',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '& .MuiInputAdornment-positionEnd.MuiInputAdornment-outlined': {
            paddingRight: 6,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: alpha(themeColors.white, 0.2),
          },
          '&.Mui-focused:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: themeColors.secondary,
          },
        },
      },
    },
    MuiListSubheader: {
      styleOverrides: {
        colorPrimary: {
          fontWeight: 'bold',
          lineHeight: '40px',
          fontSize: 13,
          background: themeColors.bgElevated,
          color: themeColors.textSecondary,
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        action: { marginTop: -5, marginBottom: -5 },
        title: { fontSize: 15 },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: { borderRadius: '50px' },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          backgroundColor: alpha(themeColors.white, 0.06),
          color: themeColors.textPrimary,
          borderRadius: '8px',
        },
        colorSecondary: {
          background: alpha(themeColors.white, 0.06),
          color: themeColors.textPrimary,
          '&:hover': {
            background: alpha(themeColors.white, 0.1),
          },
        },
        deleteIcon: {
          color: colors.error.light,
          '&:hover': {
            color: colors.error.main,
          },
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          backgroundColor: themeColors.bgSurface,
          '&.Mui-expanded': { margin: 0 },
          '&::before': { display: 'none' },
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: { fontSize: 14, fontWeight: 'bold' },
        colorDefault: {
          background: alpha(themeColors.white, 0.15),
          color: colors.alpha.white[100],
        },
      },
    },
    MuiAvatarGroup: {
      styleOverrides: {
        root: { alignItems: 'center' },
        avatar: {
          background: alpha(themeColors.white, 0.08),
          fontSize: 13,
          color: themeColors.textSecondary,
          fontWeight: 'bold',
          '&:first-of-type': { border: 0, background: 'transparent' },
        },
      },
    },
    MuiListItemAvatar: {
      styleOverrides: {
        alignItemsFlexStart: { marginTop: 0 },
      },
    },
    MuiPaginationItem: {
      styleOverrides: {
        page: {
          fontSize: 13,
          fontWeight: 'bold',
          transition: 'all .2s',
        },
        textPrimary: {
          '&.Mui-selected': {
            boxShadow: colors.shadows.primary,
          },
          '&.MuiButtonBase-root:hover': {
            background: alpha(themeColors.white, 0.06),
          },
          '&.Mui-selected.MuiButtonBase-root:hover': {
            background: colors.primary.main,
          },
        },
      },
    },
    MuiButton: {
      defaultProps: { disableRipple: true },
      styleOverrides: {
        root: {
          fontWeight: 'bold',
          textTransform: 'none',
          paddingLeft: 16,
          paddingRight: 16,
          borderRadius: '12px',
          '.MuiSvgIcon-root': { transition: 'all .2s' },
        },
        endIcon: { marginRight: -8 },
        containedPrimary: {
          background: 'linear-gradient(135deg, #2979FF 0%, #00B0FF 100%)',
          color: themeColors.white,
          '&:hover': {
            background: 'linear-gradient(135deg, #1565C0 0%, #0091EA 100%)',
          },
        },
        containedSecondary: {
          backgroundColor: themeColors.success,
          color: themeColors.white,
          '&:hover': {
            backgroundColor: darken(themeColors.success, 0.1),
          },
        },
        outlinedSecondary: {
          backgroundColor: 'transparent',
          borderColor: alpha(themeColors.white, 0.15),
          color: themeColors.textPrimary,
          '&:hover, &.MuiSelected': {
            backgroundColor: alpha(themeColors.white, 0.04),
            color: themeColors.textPrimary,
          },
        },
        sizeSmall: { padding: '6px 16px', lineHeight: 1.5 },
        sizeMedium: { padding: '8px 20px' },
        sizeLarge: { padding: '11px 24px' },
        textSizeSmall: { padding: '7px 12px' },
        textSizeMedium: { padding: '9px 16px' },
        textSizeLarge: { padding: '12px 16px' },
      },
    },
    MuiButtonBase: {
      defaultProps: { disableRipple: false },
      styleOverrides: {
        root: { borderRadius: 8 },
      },
    },
    MuiToggleButton: {
      defaultProps: { disableRipple: true },
      styleOverrides: {
        root: {
          color: themeColors.textSecondary,
          background: themeColors.bgSurface,
          transition: 'all .2s',
          '&:hover, &.Mui-selected, &.Mui-selected:hover': {
            color: themeColors.white,
            background: themeColors.primary,
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: 8,
          color: themeColors.textSecondary,
          '& .MuiTouchRipple-root': { borderRadius: 8 },
          '&:hover': {
            backgroundColor: alpha(themeColors.white, 0.06),
          },
        },
        sizeSmall: { padding: 4 },
      },
    },
    MuiListItemText: {
      styleOverrides: { root: { margin: 0 } },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          '& .MuiTouchRipple-root': { opacity: 0.3 },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          background: 'rgba(255,255,255,0.06)',
          border: 0,
          height: 1,
        },
        vertical: {
          height: 'auto',
          width: 1,
          '&.MuiDivider-flexItem.MuiDivider-fullWidth': { height: 'auto' },
          '&.MuiDivider-absolute.MuiDivider-fullWidth': { height: '100%' },
        },
        withChildren: {
          '&:before, &:after': { border: 0 },
        },
        wrapper: {
          background: themeColors.bgSurface,
          fontWeight: 'bold',
          height: 24,
          lineHeight: '24px',
          marginTop: -12,
          color: 'inherit',
          textTransform: 'uppercase',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          padding: 0,
          backgroundColor: themeColors.bgSurface,
          backgroundImage: 'none',
        },
        elevation0: { boxShadow: 'none' },
        elevation: { boxShadow: colors.shadows.card },
        elevation2: { boxShadow: colors.shadows.cardSm },
        elevation24: { boxShadow: colors.shadows.cardLg },
        outlined: { boxShadow: colors.shadows.card },
      },
    },
    MuiLink: {
      defaultProps: { underline: 'hover' },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: { borderRadius: 6, height: 6 },
      },
    },
    MuiSlider: {
      styleOverrides: {
        root: {
          '& .MuiSlider-valueLabelCircle, .MuiSlider-valueLabelLabel': {
            transform: 'none',
          },
          '& .MuiSlider-valueLabel': {
            borderRadius: 6,
            background: themeColors.bgElevated,
            color: themeColors.textPrimary,
          },
        },
      },
    },
    MuiList: {
      styleOverrides: {
        root: {
          padding: 0,
          '& .MuiListItem-button': {
            transition: 'all .2s',
            '& > .MuiSvgIcon-root': { minWidth: 34 },
            '& .MuiTouchRipple-root': { opacity: 0.2 },
          },
          '& .MuiListItem-root.MuiButtonBase-root.Mui-selected': {
            backgroundColor: alpha(themeColors.primary, 0.15),
          },
          '& .MuiMenuItem-root.MuiButtonBase-root:active': {
            backgroundColor: alpha(themeColors.primary, 0.15),
          },
          '& .MuiMenuItem-root.MuiButtonBase-root .MuiTouchRipple-root': {
            opacity: 0.2,
          },
        },
        padding: {
          padding: '12px',
          '& .MuiListItem-button': {
            borderRadius: 8,
            margin: '1px 0',
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: { height: 38, minHeight: 38, overflow: 'visible' },
        indicator: {
          height: 38,
          minHeight: 38,
          borderRadius: 8,
          background: 'linear-gradient(135deg, #2979FF, #00B0FF)',
          border: 'none',
          boxShadow: '0px 2px 10px rgba(232,52,26,0.35)',
        },
        scrollableX: { overflow: 'visible !important' },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          padding: 0,
          height: 38,
          minHeight: 38,
          borderRadius: 8,
          transition: 'color .2s',
          textTransform: 'capitalize',
          '&.MuiButtonBase-root': {
            minWidth: 'auto',
            paddingLeft: 20,
            paddingRight: 20,
            marginRight: 4,
          },
          '&.Mui-selected, &.Mui-selected:hover': {
            color: colors.alpha.white[100],
            zIndex: 5,
          },
          '&:hover': {
            color: colors.alpha.white[70],
          },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: { padding: 12, backgroundColor: themeColors.bgElevated },
        list: {
          padding: 12,
          '& .MuiMenuItem-root.MuiButtonBase-root': {
            fontSize: 14,
            marginTop: 1,
            marginBottom: 1,
            transition: 'all .2s',
            color: themeColors.textSecondary,
            '& .MuiTouchRipple-root': { opacity: 0.2 },
            '&:hover, &:active, &.active, &.Mui-selected': {
              color: themeColors.textPrimary,
              background: alpha(themeColors.white, 0.06),
            },
          },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          background: 'transparent',
          transition: 'all .2s',
          '&:hover, &:active, &.active, &.Mui-selected': {
            color: themeColors.textPrimary,
            background: alpha(themeColors.white, 0.06),
          },
          '&.Mui-selected:hover': {
            background: alpha(themeColors.white, 0.08),
          },
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          '&.MuiButtonBase-root': {
            color: themeColors.textSecondary,
            '&:hover, &:active, &.active, &.Mui-selected': {
              color: themeColors.textPrimary,
              background: alpha(themeColors.white, 0.06),
            },
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        tag: { margin: 1 },
        root: {
          '.MuiAutocomplete-inputRoot.MuiOutlinedInput-root .MuiAutocomplete-endAdornment': {
            right: 14,
          },
        },
        clearIndicator: {
          background: alpha(themeColors.error, 0.1),
          color: colors.error.main,
          marginRight: 8,
          '&:hover': {
            background: alpha(themeColors.error, 0.15),
            color: colors.error.dark,
          },
        },
        popupIndicator: {
          color: themeColors.textSecondary,
          '&:hover': {
            background: alpha(themeColors.primary, 0.1),
            color: themeColors.primary,
          },
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        toolbar: {
          '& .MuiIconButton-root': { padding: 8 },
        },
        select: {
          '&:focus': { backgroundColor: 'transparent' },
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: { minHeight: '0 !important', padding: '0 !important' },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        head: { background: themeColors.bgElevated },
        root: {
          transition: 'background-color .2s',
          '&.MuiTableRow-hover:hover': {
            backgroundColor: alpha(themeColors.white, 0.03),
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottomColor: 'rgba(255,255,255,0.06)',
          fontSize: 14,
          color: themeColors.textPrimary,
        },
        head: {
          textTransform: 'uppercase',
          fontSize: 13,
          fontWeight: 'bold',
          color: themeColors.textSecondary,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        message: { lineHeight: 1.5, fontSize: 14 },
        standardInfo: { color: colors.info.main },
        action: { color: themeColors.textSecondary },
      },
    },
    MuiTimelineDot: {
      styleOverrides: {
        root: {
          margin: 0,
          zIndex: 5,
          position: 'absolute',
          top: '50%',
          marginTop: -6,
          left: -6,
        },
        outlined: {
          backgroundColor: themeColors.bgSurface,
          boxShadow: '0 0 0 6px ' + themeColors.bgSurface,
        },
        outlinedPrimary: {
          backgroundColor: themeColors.bgSurface,
          boxShadow: '0 0 0 6px ' + themeColors.bgSurface,
        },
      },
    },
    MuiTimelineConnector: {
      styleOverrides: {
        root: {
          position: 'absolute',
          height: '100%',
          top: 0,
          borderRadius: 50,
          backgroundColor: 'rgba(255,255,255,0.08)',
        },
      },
    },
    MuiTimelineItem: {
      styleOverrides: {
        root: {
          minHeight: 0,
          padding: '8px 0',
          '&:before': { display: 'none' },
        },
        missingOppositeContent: {
          '&:before': { display: 'none' },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: themeColors.bgElevated,
          padding: '8px 16px',
          fontSize: 13,
          color: themeColors.textPrimary,
          boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
          borderRadius: 8,
        },
        arrow: {
          color: themeColors.bgElevated,
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          height: 33,
          overflow: 'visible',
          '& .MuiButtonBase-root': {
            position: 'absolute',
            padding: 6,
            transition: 'left 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,transform 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
          },
          '& .MuiIconButton-root': { borderRadius: 100 },
          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
            opacity: 0.3,
          },
        },
        thumb: {
          border: '1px solid ' + alpha(themeColors.white, 0.15),
          boxShadow: '0px 4px 8px rgba(0,0,0,0.3)',
        },
        track: {
          backgroundColor: alpha(themeColors.white, 0.08),
          border: '1px solid ' + alpha(themeColors.white, 0.1),
          boxShadow: 'inset 0px 1px 1px rgba(0,0,0,0.2)',
          opacity: 1,
        },
        colorPrimary: {
          '& .MuiSwitch-thumb': {
            backgroundColor: themeColors.bgElevated,
          },
          '&.Mui-checked .MuiSwitch-thumb': {
            backgroundColor: themeColors.success,
          },
        },
      },
    },
    MuiStepper: {
      styleOverrides: {
        root: {
          paddingTop: 20,
          paddingBottom: 20,
          background: themeColors.bgSurface,
        },
      },
    },
    MuiStepIcon: {
      styleOverrides: {
        root: {
          '&.MuiStepIcon-completed': {
            color: colors.success.main,
          },
        },
      },
    },
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          h1: 'h1',
          h2: 'h2',
          h3: 'div',
          h4: 'div',
          h5: 'div',
          h6: 'div',
          subtitle1: 'div',
          subtitle2: 'div',
          body1: 'div',
          body2: 'div',
        },
      },
      styleOverrides: {
        gutterBottom: { marginBottom: 4 },
        paragraph: { fontSize: 17, lineHeight: 1.7 },
      },
    },
  },
  typography: {
    fontFamily: '"Nunito Sans", "Helvetica", "Arial", sans-serif',
    h1: { fontFamily: '"Baloo 2", cursive', fontWeight: 800, fontSize: 28, lineHeight: 1.2, color: themeColors.textPrimary },
    h2: { fontFamily: '"Baloo 2", cursive', fontWeight: 700, fontSize: 22, lineHeight: 1.3, color: themeColors.textPrimary },
    h3: { fontFamily: '"Baloo 2", cursive', fontWeight: 700, fontSize: 18, lineHeight: 1.4, color: themeColors.textPrimary },
    h4: { fontFamily: '"Baloo 2", cursive', fontWeight: 700, fontSize: 15, lineHeight: 1.4, color: themeColors.textPrimary },
    h5: { fontFamily: '"Baloo 2", cursive', fontWeight: 600, fontSize: 14, color: themeColors.textPrimary },
    h6: { fontFamily: '"Baloo 2", cursive', fontWeight: 600, fontSize: 15, color: themeColors.textPrimary },
    body1: { fontSize: 14, lineHeight: 1.5, color: themeColors.textPrimary },
    body2: { fontSize: 13, lineHeight: 1.5, color: themeColors.textSecondary },
    button: { fontWeight: 600 },
    caption: { fontSize: 11, fontWeight: 500, lineHeight: 1.4, color: themeColors.textSecondary },
    subtitle1: { fontSize: 14, color: themeColors.textSecondary },
    subtitle2: { fontWeight: 400, fontSize: 15, color: themeColors.textSecondary },
    overline: { fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' },
  },
  shadows: [
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
  ],
});
